import { Component, OnInit, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { PaneComponent, TitleComponent } from '@rocksoft/cdk';
import { environment } from '@rocksoft/environment';
import {
  LocationAclGroups,
  LocationAclMembers,
  OperationFlags,
  RoomAclGroups,
  RoomAclMembers,
} from '@rocksoft/locations-sdk';
import { UserGroupAclGroups, UserGroupAclMembers } from '@rocksoft/security-sdk';
import type { NamedAclParent } from '../../types/acl-named.types';

type AclMembersAndGroups =
  | UserGroupAclGroups
  | UserGroupAclMembers
  | LocationAclMembers
  | LocationAclGroups
  | RoomAclGroups
  | RoomAclMembers;

@Component({
  selector: 'era-acl-permission-details',
  templateUrl: './acl-permission-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TitleComponent, PaneComponent, ReactiveFormsModule],
})
export class AclPermissionDetailsComponent implements OnInit {
  private readonly formBuilder = inject(UntypedFormBuilder);

  public readonly acl = input<AclMembersAndGroups>();
  public readonly aclParents = input<NamedAclParent[]>([]);

  public readonly submitFormEvent = output<AclMembersAndGroups>();

  public operationsForm: UntypedFormGroup;
  public operations: string[] = [];

  public readonly environment = environment;

  constructor() {
    effect(() => {
      const acl = this.acl();
      const aclParents = this.aclParents();

      if (!acl) {
        return;
      }

      this.initialiseForm(acl, aclParents);
    });
  }

  ngOnInit() {
    this.initialiseForm(this.acl(), this.aclParents());
  }

  private initialiseForm(acl: AclMembersAndGroups | undefined, aclParents: NamedAclParent[]): void {
    if (!acl) {
      return;
    }

    this.operationsForm = this.formBuilder.group({});
    this.operations = [];

    for (const operation of Object.values(OperationFlags)) {
      // Guard, the 'None' operation isn't configurable
      if (operation == OperationFlags.None) {
        continue;
      }

      this.operations.push(operation);

      const allow: boolean = [...(acl.allow ?? [])].includes(operation);
      const deny: boolean = [...(acl.deny ?? [])].includes(operation);

      if (allow || deny) {
        const permission = allow ? 'allow' : 'deny';
        this.operationsForm.addControl(operation, new UntypedFormControl(permission, Validators.required));
      } else {
        // The operation isn't set yet on this level, check if the parents have it set.
        const permission = this.getParentPermissions(operation, aclParents);
        this.operationsForm.addControl(operation, new UntypedFormControl(permission, Validators.required));
      }
    }
  }

  private getParentPermissions(flag: OperationFlags, aclParents: NamedAclParent[]): string | undefined {
    const deny = aclParents.some((x) => [...(x.deny ?? [])].includes(flag));
    const allow = aclParents.some((x) => [...(x.allow ?? [])].includes(flag));

    if (deny) {
      // Deny has an higher prio then allow
      this.operationsForm.addControl(flag, new UntypedFormControl('inheritDeny', Validators.required));
    } else if (allow) {
      this.operationsForm.addControl(flag, new UntypedFormControl('inheritAllow', Validators.required));
    } else {
      // 'inherit' means not set
      return 'inherit';
    }
  }

  public submitForm() {
    const allow = new Set<OperationFlags>();
    const deny = new Set<OperationFlags>();

    for (const operation of Object.values(OperationFlags)) {
      // Guard, the 'None' operation isn't configurable
      if (operation == OperationFlags.None) {
        continue;
      }

      // We only care about the 'allow' and 'deny' values. The
      // others all come down to "not set on this level".
      if (this.operationsForm.get(operation)?.value === 'allow') {
        allow.add(operation);
      } else if (this.operationsForm.get(operation)?.value === 'deny') {
        deny.add(operation);
      }
    }

    const current = this.acl();
    if (!current) {
      return;
    }

    const updatedAcl: AclMembersAndGroups = { ...current, allow, deny };
    this.submitFormEvent.emit(updatedAcl);
  }
}
